import type { ReactNode } from "react";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import {
  BREAKER_PANEL,
  EmptyMachine,
  LEAKING_VALVE,
  MachineBackdrop,
} from "@volca/ui/components/ui/empty-machine";
import { Stack } from "@volca/ui/components/ui/stack";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";

interface ErrorStateProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  retrying?: boolean;
  illustration?: "responsive" | "compact" | "none";
  className?: string;
}

export const ErrorState = ({
  title,
  description,
  children,
  retrying = false,
  illustration = "responsive",
  className,
}: ErrorStateProps) => (
  <Empty
    role="alert"
    data-machine-host
    data-illustration={illustration}
    className={cn(
      emptyStateVariants({ layout: "panel" }),
      "@container/error-state relative isolate justify-center-safe overflow-clip",
      className,
    )}
  >
    {illustration !== "none" && (
      <MachineBackdrop
        floor={BREAKER_PANEL.floor}
        className="hidden @2xs/error-state:block"
      />
    )}
    <Stack
      gap={6}
      align="center"
      className={cn("w-full max-w-176", {
        "@2xl/error-state:flex-row @2xl/error-state:gap-12":
          illustration === "responsive",
      })}
    >
      {illustration !== "none" && (
        <>
          {illustration === "responsive" && (
            <EmptyMedia className="m-0 hidden @md/error-state:flex">
              <EmptyMachine
                art={BREAKER_PANEL}
                active={retrying}
                className="h-56 @2xl/error-state:h-72"
              />
            </EmptyMedia>
          )}
          <EmptyMedia
            className={cn("m-0 hidden @2xs/error-state:flex", {
              "@md/error-state:hidden": illustration === "responsive",
            })}
          >
            <EmptyMachine
              art={LEAKING_VALVE}
              active={retrying}
              className="h-auto w-36"
            />
          </EmptyMedia>
        </>
      )}
      <Stack
        gap={5}
        align="center"
        className={cn("w-full max-w-88 text-center", {
          "@2xl/error-state:items-start @2xl/error-state:text-start":
            illustration === "responsive",
        })}
      >
        <EmptyHeader
          className={cn("w-full max-w-none gap-3", {
            "@2xl/error-state:items-start": illustration === "responsive",
          })}
        >
          <EmptyTitle
            className={cn("text-base leading-snug", {
              "@md/error-state:text-xl @md/error-state:font-semibold":
                illustration === "responsive",
            })}
          >
            {title}
          </EmptyTitle>
          {description && <EmptyDescription>{description}</EmptyDescription>}
        </EmptyHeader>
        {children && (
          <EmptyContent
            data-machine-trigger
            className={cn("w-full", {
              "@2xl/error-state:items-start": illustration === "responsive",
            })}
          >
            {children}
          </EmptyContent>
        )}
      </Stack>
    </Stack>
  </Empty>
);
